import { buildApiUrl } from '~/app/lib/api-config';
import type {
  AssetModelResponseDto,
  AssetTypeManufacturerModelResponseDto,
  CreateAssetModelRequestDto,
  CreateAssetTypeManufacturerModelRequestDto,
  CreateManufacturerRequestDto,
  ListAssetModelsResponseDto,
  ListAssetTypeManufacturerModelsResponseDto,
  ListManufacturersResponseDto,
  ManufacturerResponseDto,
  UpdateAssetModelRequestDto,
  UpdateAssetTypeManufacturerModelRequestDto,
  UpdateManufacturerRequestDto,
} from '@evimo/types';

type ListOpts = { limit?: number; page?: number; q?: string };

async function parseError(response: Response): Promise<never> {
  const errorPayload = await response
    .json()
    .catch(() => ({ message: response.statusText }));
  throw new Error(
    (errorPayload as { message?: string }).message ||
      `Request failed (${response.status})`,
  );
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(buildApiUrl(path), {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    await parseError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function toQuery(opts?: ListOpts): string {
  const params = new URLSearchParams();
  params.set('page', String(opts?.page ?? 1));
  params.set('limit', String(opts?.limit ?? 1000));
  if (opts?.q) params.set('q', opts.q);
  return params.toString();
}

export const ManufacturerCatalogApi = {
  list(opts?: ListOpts): Promise<ListManufacturersResponseDto> {
    return apiFetch(`/api/v1/manufacturers?${toQuery(opts)}`);
  },
  create(dto: CreateManufacturerRequestDto): Promise<ManufacturerResponseDto> {
    return apiFetch('/api/v1/manufacturers', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  update(
    id: string,
    dto: UpdateManufacturerRequestDto,
  ): Promise<ManufacturerResponseDto> {
    return apiFetch(`/api/v1/manufacturers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  delete(id: string): Promise<void> {
    return apiFetch(`/api/v1/manufacturers/${id}`, { method: 'DELETE' });
  },
};

export const AssetModelCatalogApi = {
  list(opts?: ListOpts): Promise<ListAssetModelsResponseDto> {
    return apiFetch(`/api/v1/asset-models?${toQuery(opts)}`);
  },
  create(dto: CreateAssetModelRequestDto): Promise<AssetModelResponseDto> {
    return apiFetch('/api/v1/asset-models', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  update(
    id: string,
    dto: UpdateAssetModelRequestDto,
  ): Promise<AssetModelResponseDto> {
    return apiFetch(`/api/v1/asset-models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  delete(id: string): Promise<void> {
    return apiFetch(`/api/v1/asset-models/${id}`, { method: 'DELETE' });
  },
};

export const AssetTypeManufacturerModelCatalogApi = {
  list(opts?: ListOpts): Promise<ListAssetTypeManufacturerModelsResponseDto> {
    return apiFetch(`/api/v1/asset-type-manufacturer-models?${toQuery(opts)}`);
  },
  listByAssetType(
    assetTypeId: string,
  ): Promise<AssetTypeManufacturerModelResponseDto[]> {
    return apiFetch(
      `/api/v1/asset-type-manufacturer-models/by-asset-type/${assetTypeId}`,
    );
  },
  create(
    dto: CreateAssetTypeManufacturerModelRequestDto,
  ): Promise<AssetTypeManufacturerModelResponseDto> {
    return apiFetch('/api/v1/asset-type-manufacturer-models', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },
  update(
    id: string,
    dto: UpdateAssetTypeManufacturerModelRequestDto,
  ): Promise<AssetTypeManufacturerModelResponseDto> {
    return apiFetch(`/api/v1/asset-type-manufacturer-models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
  delete(id: string): Promise<void> {
    return apiFetch(`/api/v1/asset-type-manufacturer-models/${id}`, {
      method: 'DELETE',
    });
  },
};
